import React, { useState, useEffect } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { FaBars, FaTimes } from 'react-icons/fa';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <NavContainer $isScrolled={isScrolled}>
      <NavContent>
        <Logo onClick={() => scrollToSection('header')}>
          <LogoText>M30</LogoText>
          <LogoAccent>FITNESS</LogoAccent>
        </Logo>

        <NavLinks $isOpen={isMobileMenuOpen}>
          <NavLink onClick={() => scrollToSection('header')}>
            <NavLinkText>Início</NavLinkText>
          </NavLink>
          <NavLink onClick={() => scrollToSection('servicos')}>
            <NavLinkText>Sobre</NavLinkText>
          </NavLink>
          <NavLink onClick={() => scrollToSection('precos')}>
            <NavLinkText>Preços</NavLinkText>
          </NavLink>
          <NavLink onClick={() => scrollToSection('galeria')}>
            <NavLinkText>Galeria</NavLinkText>
          </NavLink>
          <NavLink onClick={() => scrollToSection('aulas')}>
            <NavLinkText>Aulas</NavLinkText>
          </NavLink>
          <NavLink onClick={() => scrollToSection('contato')}>
            <NavLinkText>Contato</NavLinkText>
          </NavLink>
        </NavLinks>

        <CTAButton onClick={() => scrollToSection('precos')}>
          <ButtonGlow />
          <span>MATRICULE-SE</span>
        </CTAButton>

        <MobileMenuButton onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
          {isMobileMenuOpen ? <FaTimes /> : <FaBars />}
        </MobileMenuButton>
      </NavContent>

      <MobileMenu $isOpen={isMobileMenuOpen}>
        <MobileNavLink onClick={() => scrollToSection('header')}>Início</MobileNavLink>
        <MobileNavLink onClick={() => scrollToSection('servicos')}>Sobre</MobileNavLink>
        <MobileNavLink onClick={() => scrollToSection('precos')}>Preços</MobileNavLink>
        <MobileNavLink onClick={() => scrollToSection('galeria')}>Galeria</MobileNavLink>
        <MobileNavLink onClick={() => scrollToSection('aulas')}>Aulas</MobileNavLink>
        <MobileNavLink onClick={() => scrollToSection('contato')}>Contato</MobileNavLink>
        <MobileCTAButton onClick={() => scrollToSection('precos')}>MATRICULE-SE</MobileCTAButton>
      </MobileMenu>
    </NavContainer>
  );
};

export default Navbar;

const slideDown = keyframes`
  from {
    transform: translateY(-100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const glowPulse = keyframes`
  0%, 100% {
    opacity: 0.5;
    transform: scale(1);
  }
  50% {
    opacity: 0.8;
    transform: scale(1.1);
  }
`;

const NavContainer = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  animation: ${slideDown} 0.6s ease-out;
  
  ${({ $isScrolled }) => $isScrolled ? css`
    background: rgba(10, 10, 10, 0.95);
    backdrop-filter: blur(20px);
    box-shadow: 0 4px 30px rgba(0, 0, 0, 0.5),
                0 1px 0 rgba(229, 168, 0, 0.1);
  ` : css`
    background: transparent;
  `}
`;

const NavContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 15px 30px;

  @media (max-width: 768px) {
    padding: 15px 20px;
  }
`;

const Logo = styled.div`
  display: flex;
  align-items: baseline;
  cursor: pointer;
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.05);
  }
`;

const LogoText = styled.span`
  font-family: 'Orbitron', sans-serif;
  font-size: 2rem;
  font-weight: 900;
  color: #fff;
  letter-spacing: 2px;
`;

const LogoAccent = styled.span`
  font-family: 'Orbitron', sans-serif;
  font-size: 1rem;
  font-weight: 600;
  color: var(--primary-color);
  margin-left: 5px;
  letter-spacing: 3px;
`;

const NavLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  @media (max-width: 900px) {
    display: none;
  }
`;

const NavLink = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 10px 15px;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    width: 0;
    height: 2px;
    background: var(--gradient-primary);
    transition: all 0.3s ease;
    transform: translateX(-50%);
  }

  &:hover::before {
    width: 80%;
  }

  &:hover span {
    color: var(--primary-color);
    text-shadow: 0 0 20px var(--primary-glow);
  }
`;

const NavLinkText = styled.span`
  font-family: 'Rajdhani', sans-serif;
  font-size: 1rem;
  font-weight: 600;
  color: #fff;
  text-transform: uppercase;
  letter-spacing: 1px;
  transition: all 0.3s ease;
`;

const CTAButton = styled.button`
  position: relative;
  background: var(--gradient-primary);
  border: none;
  padding: 12px 28px;
  border-radius: 50px;
  cursor: pointer;
  overflow: hidden;
  transition: all 0.3s ease;

  span {
    position: relative;
    z-index: 2;
    font-family: 'Orbitron', sans-serif;
    font-size: 0.85rem;
    font-weight: 700;
    color: #000;
    letter-spacing: 1px;
  }

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 30px var(--primary-glow);
  }

  &:active {
    transform: translateY(-1px);
  }

  @media (max-width: 900px) {
    display: none;
  }
`;

const ButtonGlow = styled.div`
  position: absolute;
  inset: -2px;
  background: var(--gradient-primary);
  border-radius: 50px;
  z-index: 0;
  opacity: 0;
  filter: blur(15px);
  animation: ${glowPulse} 2s ease-in-out infinite;
  transition: opacity 0.3s ease;

  ${CTAButton}:hover & {
    opacity: 1;
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  color: var(--primary-color);
  font-size: 1.5rem;
  cursor: pointer;
  padding: 5px;
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.1);
  }

  @media (max-width: 900px) {
    display: block;
  }
`;

const MobileMenu = styled.div`
  display: none;
  
  @media (max-width: 900px) {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    padding: 20px;
    background: rgba(10, 10, 10, 0.98);
    backdrop-filter: blur(20px);
    border-top: 1px solid rgba(229, 168, 0, 0.1);
    max-height: ${({ $isOpen }) => $isOpen ? '500px' : '0'};
    opacity: ${({ $isOpen }) => $isOpen ? '1' : '0'};
    overflow: hidden;
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  }
`;

const MobileNavLink = styled.button`
  background: none;
  border: none;
  font-family: 'Rajdhani', sans-serif;
  font-size: 1.1rem;
  font-weight: 600;
  color: #fff;
  text-transform: uppercase;
  letter-spacing: 2px;
  padding: 12px 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 100%;
  text-align: center;

  &:hover {
    color: var(--primary-color);
    background: rgba(229, 168, 0, 0.05);
  }
`;

const MobileCTAButton = styled.button`
  background: var(--gradient-primary);
  border: none;
  padding: 15px 40px;
  border-radius: 50px;
  font-family: 'Orbitron', sans-serif;
  font-size: 0.9rem;
  font-weight: 700;
  color: #000;
  letter-spacing: 1px;
  cursor: pointer;
  margin-top: 10px;
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 10px 30px var(--primary-glow);
  }
`;


